import { ChevronRight, Check, Pencil, Sparkles, Trash2, Zap } from 'lucide-react';
import {
  DailyTaskActionType,
  DAILY_TASK_ACTION_TYPE_LABELS,
  DAY_KIND_LABELS,
} from '../services/dailyTaskTemplateTypes';
import type { DailyTaskTemplateFirestore } from '../services/dailyTaskTemplateTypes';

const COLOR_BORDER: Record<string, string> = {
  yellow: 'border-l-yellow-400',
  blue: 'border-l-blue-400',
  green: 'border-l-green-400',
  pink: 'border-l-pink-400',
};

interface DailyTaskTemplateCardProps {
  template: DailyTaskTemplateFirestore;
  onEdit: () => void;
  onDelete: () => void;
}

export const DailyTaskTemplateCard = ({
  template,
  onEdit,
  onDelete,
}: DailyTaskTemplateCardProps) => {
  const borderColor = COLOR_BORDER[template.colorVariant] ?? 'border-l-gray-300';
  const isDeepLink = template.actionType === DailyTaskActionType.DEEP_LINK;
  const hasTitle = !!template.title;
  const hasEmoji = !!template.emoji;
  const isAiGenerated = !hasTitle || !hasEmoji;

  return (
    <div
      className={`bg-white rounded-lg shadow-sm border border-gray-200 border-l-4 ${borderColor} hover:shadow-md transition-shadow`}
    >
      <div className="flex items-start gap-3 p-3">
        {/* Emoji */}
        <span className={`text-2xl leading-none mt-0.5 shrink-0 ${!hasEmoji ? 'opacity-40 grayscale' : ''}`}>
          {template.emoji || '✨'}
        </span>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              {hasTitle ? (
                <h4 className="text-sm font-semibold text-gray-800 truncate">{template.title}</h4>
              ) : (
                <h4 className="text-sm font-semibold text-gray-400 italic truncate">
                  {template.textTemplatingPrompt
                    ? template.textTemplatingPrompt.slice(0, 60) + (template.textTemplatingPrompt.length > 60 ? '...' : '')
                    : 'No title — AI will generate'}
                </h4>
              )}
              {isAiGenerated && (
                <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-600 font-medium mt-0.5">
                  <Sparkles size={10} />
                  {!hasTitle && !hasEmoji ? 'Title & emoji by AI' : !hasTitle ? 'Title by AI' : 'Emoji by AI'}
                </span>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                onClick={onEdit}
                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                title="Edit"
              >
                <Pencil size={14} />
              </button>
              <button
                type="button"
                onClick={onDelete}
                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                title="Delete"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>

          {/* Metadata pills */}
          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            {/* Published status */}
            <span
              className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                template.isPublished
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-500'
              }`}
            >
              {template.isPublished ? 'Published' : 'Draft'}
            </span>

            {/* Energy */}
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-yellow-50 text-yellow-700 text-[10px] font-medium">
              <Zap size={9} fill="currentColor" />
              {template.energy}
            </span>

            {/* Steps */}
            {template.totalSteps > 1 && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[10px] font-medium">
                {template.totalSteps} steps
              </span>
            )}

            {/* Action type */}
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[10px] font-medium">
              {isDeepLink ? <ChevronRight size={9} /> : <Check size={9} />}
              {DAILY_TASK_ACTION_TYPE_LABELS[template.actionType]}
            </span>

            {/* Day Kind Tags */}
            {template.dayKindTags && template.dayKindTags.length > 0 ? (
              template.dayKindTags.map((kind) => (
                <span key={kind} className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-teal-50 text-teal-700 text-[10px] font-medium">
                  {DAY_KIND_LABELS[kind]}
                </span>
              ))
            ) : (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-teal-50 text-teal-600 text-[10px] font-medium">
                All day kinds
              </span>
            )}
          </div>

          {/* Tags */}
          {template.ocdSubtypeTags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {template.ocdSubtypeTags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-purple-50 text-purple-600 text-[10px]"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
